import {
  PORT_COST_MISSING_PRIMARY_ATTRIBUTE_KEY,
  PORT_COST_MISSING_PORT_TYPE_KEY,
} from '../../hooks/usePortCostEstimates';
import type { LatestExternalPortCostEstimates } from '../../hooks/usePortCostEstimates';
import type { VesselAttributeId } from '../../../graphql';
import { PortType } from '@seaber/common/graphql/types';

export type LatestHistoricalPortCostEstimates = {
  cost: number | undefined;
  currency: string;
};

function findEffectiveLatestPortCostHistory(
  map: LatestExternalPortCostEstimates,
  portName: string,
  primaryAttributeId: string,
  portType: string | null,
): ReturnType<typeof map.get> | undefined {
  const portFunctionKey = portType ?? PORT_COST_MISSING_PORT_TYPE_KEY;

  // 1: Find match by specific port type
  const portCostEstimate = map.get(
    portName,
    primaryAttributeId,
    portFunctionKey,
  );
  if (portCostEstimate) {
    return portCostEstimate;
  }

  if (portType === PortType.M) {
    // try 'L'
    const portCostLoad = map.get(portName, primaryAttributeId, PortType.L);
    if (portCostLoad) {
      return portCostLoad;
    }
    // try 'D'
    const portCostDischarge = map.get(portName, primaryAttributeId, PortType.D);
    if (portCostDischarge) {
      return portCostDischarge;
    }
  }

  return map.get(portName, primaryAttributeId, PORT_COST_MISSING_PORT_TYPE_KEY);
}

export function getLatestHistoricalPortCost(
  portCallLocationName: string,
  primaryVesselAttributeId: VesselAttributeId | null | undefined,
  latestByPrimaryAttributeId: LatestExternalPortCostEstimates,
  portType?: PortType,
): LatestHistoricalPortCostEstimates | undefined {
  const portName = portCallLocationName.toUpperCase();
  const primaryAttributeKey =
    primaryVesselAttributeId?.toString() ??
    PORT_COST_MISSING_PRIMARY_ATTRIBUTE_KEY;

  let latestByAttribute = findEffectiveLatestPortCostHistory(
    latestByPrimaryAttributeId,
    portName,
    primaryAttributeKey,
    portType ?? null,
  );

  if (!latestByAttribute) {
    latestByAttribute = findEffectiveLatestPortCostHistory(
      latestByPrimaryAttributeId,
      portName,
      PORT_COST_MISSING_PRIMARY_ATTRIBUTE_KEY,
      portType ?? null,
    );
  }

  if (latestByAttribute && latestByAttribute.portCost !== undefined) {
    return {
      cost: Math.round(latestByAttribute.portCost),
      currency: latestByAttribute.currency,
    };
  }

  return undefined;
}
